import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { NavIconBump, NavActivePill } from './nav/NavTabPolish';
import { useScrollNavCompact } from '../hooks/useScrollNavCompact';
import { jumpToTop } from '../utils/jumpToTop';
import UnreadBadge from './UnreadBadge';
import { prefetchAllureMapStyle } from './guest-map/mapStyleUtils';
import { NavProfileAvatar } from './nav/NavProfileAvatar';
import { useTheme } from '../contexts/ThemeContext';
import { tapVibrate } from '../utils/hapticFeedback';

const ACTIVE_COLOR = 'var(--accent-purple)';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MESSAGE_BADGE_STORAGE_KEY = 'allure-guest-message-nav-ack-at';

const QrIcon = ({ color }) => (
  <svg width="28" height="26" viewBox="0 0 28 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.375 7.74996V3.29163C1.375 2.18706 2.27043 1.29163 3.375 1.29163H8.25M19.25 1.29163L23.375 1.29163C24.8938 1.29163 26.125 2.44822 26.125 3.87496V7.74996M26.125 18.0833V21.9583C26.125 23.385 24.8938 24.5416 23.375 24.5416H19.25M8.25 24.5416H4.125C2.60622 24.5416 1.375 23.385 1.375 21.9583L1.375 18.0833M22.6875 12.9166H16.125C15.5727 12.9166 15.125 13.3643 15.125 13.9166V20.6666M15.125 9.04163C17.2729 9.04163 15.7271 9.04163 17.875 9.04163M19.25 5.16663H21C21.5523 5.16663 22 5.61434 22 6.16663V7.74996M15.125 5.16663V5.81246M5.5 5.16663H11V10.3333H5.5V5.16663ZM5.5 15.5H11V20.6666H5.5L5.5 15.5ZM19.25 17.4375H22.6875V20.6666H19.25V17.4375Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MapIcon = ({ color }) => (
  <svg width="30" height="29" viewBox="0 0 30 29" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26.25 12.0834C26.25 20.5417 15 27.7917 15 27.7917C15 27.7917 3.75 20.5417 3.75 12.0834C3.75 9.19915 4.93526 6.43305 7.04505 4.39359C9.15483 2.35413 12.0163 1.20837 15 1.20837C17.9837 1.20837 20.8452 2.35413 22.955 4.39359C25.0647 6.43305 26.25 9.19915 26.25 12.0834Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 15.7084C17.0711 15.7084 18.75 14.0854 18.75 12.0834C18.75 10.0813 17.0711 8.45837 15 8.45837C12.9289 8.45837 11.25 10.0813 11.25 12.0834C11.25 14.0854 12.9289 15.7084 15 15.7084Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const LogoIcon = ({ isActive }) => (
  <img
    src={isActive ? '/allure-logo.png' : '/allure-logo-mono.png'}
    alt="Allure"
    width={32}
    height={28}
    style={{ display: 'block', objectFit: 'contain' }}
  />
);

const ChatIcon = ({ color }) => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.23933 15.9958L9.3161 17.7059C9.76655 17.8557 10.188 18.0699 10.5682 18.3389C10.8125 18.5118 11.0397 18.7074 11.2465 18.9233C11.7193 19.4165 12.0856 20.0154 12.3061 20.6907L14.0191 25.7586C15.483 30.1426 21.6498 30.0494 23.0203 25.6654L28.7823 7.1656C29.341 5.33706 28.7781 3.5703 27.6215 2.40036C26.4576 1.22291 24.6925 0.649974 22.8645 1.22703L4.30162 6.97906C-0.0587937 8.3471 -0.12108 14.5344 4.23933 15.9958Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10.7929 17.7933C11.2643 18.2647 11.7357 18.7361 12.2071 19.2075C12.4637 18.9274 12.7202 18.6472 12.9768 18.3671C14.728 16.455 16.4792 14.5429 18.2304 12.6308C21.0971 9.50076 23.9637 6.37074 26.8304 3.24072C27.0869 2.9606 27.3434 2.68049 27.6 2.40037C27.3199 2.65692 27.0398 2.91346 26.7596 3.17001C23.6296 6.03666 20.4996 8.90331 17.3696 11.77C15.4575 13.5212 13.5454 15.2724 11.6332 17.0236C11.3531 17.2802 11.073 17.5367 10.7929 17.7933Z" fill={color}/>
  </svg>
);

const DefaultAvatar = ({ isActive }) => (
  <NavProfileAvatar isActive={isActive} />
);

const tabs = [
  { id: 'qr', Icon: QrIcon, path: '/guest/qr', label: 'QR Code', testid: 'guest-bottom-nav-qr-tab' },
  { id: 'venues', Icon: MapIcon, path: '/guest/venues', label: 'Map', testid: 'guest-bottom-nav-venues-tab' },
  { id: 'home', Icon: null, path: '/guest/home', label: 'Home', testid: 'guest-bottom-nav-home-tab' },
  { id: 'chat', Icon: ChatIcon, path: '/guest/share', label: 'Chat', testid: 'guest-bottom-nav-share-tab' },
  { id: 'profile', Icon: null, path: '/guest/profile', label: 'Profile', testid: 'guest-bottom-nav-profile-tab' },
];

export const GuestBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestUnreadMarker, setLatestUnreadMarker] = useState('');
  const [lastAcknowledgedMarker, setLastAcknowledgedMarker] = useState(() => localStorage.getItem(MESSAGE_BADGE_STORAGE_KEY) || '');

  const activeTab = (() => {
    if (location.pathname.startsWith('/guest/share') || location.pathname.startsWith('/guest/chat')) return 'chat';
    return tabs.find((tab) => location.pathname.startsWith(tab.path))?.id || 'home';
  })();
  const isChatTabActive = activeTab === 'chat';

  // Warm the map style cache the moment the guest bottom nav mounts, so a
  // future tap on the Map tab resolves the style synchronously instead of
  // waiting for a CartoCDN round trip. Fire-and-forget; safe to call twice.
  useEffect(() => {
    prefetchAllureMapStyle('dark');
  }, []);

  const acknowledgeMessageBadge = useCallback((marker = latestUnreadMarker) => {
    const nextMarker = marker || new Date().toISOString();
    localStorage.setItem(MESSAGE_BADGE_STORAGE_KEY, nextMarker);
    setLastAcknowledgedMarker(nextMarker);
    setHasUnread(false);
    setUnreadCount(0);
  }, [latestUnreadMarker]);

  // Check for unread messages
  useEffect(() => {
    const checkUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/messages/conversations`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Active-Role': 'guest' }
        });
        const conversations = Array.isArray(res.data) ? res.data : [];
        const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        const unreadConversationMarkers = conversations
          .filter((conversation) => (conversation.unread_count || 0) > 0)
          .map((conversation) => conversation.last_message_at || conversation.updated_at || conversation.created_at || '');
        const newestUnreadMarker = unreadConversationMarkers.sort().at(-1) || '';
        setLatestUnreadMarker(newestUnreadMarker);
        const isUnacked = Boolean(newestUnreadMarker) && newestUnreadMarker > lastAcknowledgedMarker;
        setHasUnread(isUnacked);
        setUnreadCount(isUnacked ? totalUnread : 0);
      } catch {
        setHasUnread(false);
        setUnreadCount(0);
      }
    };
    checkUnread();
    const interval = setInterval(checkUnread, 10000);
    return () => clearInterval(interval);
  }, [lastAcknowledgedMarker]);

  useEffect(() => {
    if (isChatTabActive && latestUnreadMarker && latestUnreadMarker > lastAcknowledgedMarker) {
      acknowledgeMessageBadge(latestUnreadMarker);
    }
  }, [acknowledgeMessageBadge, isChatTabActive, lastAcknowledgedMarker, latestUnreadMarker]);

  const navCompact = useScrollNavCompact();
  const isMapPage = location.pathname.startsWith('/guest/venues');
  // On the map page the nav is always compact so the mini-map "Swipe up to
  // open" handle is clearly visible above the pill.
  const effectiveCompact = navCompact || isMapPage;
  const { isDark } = useTheme();
  const DEFAULT_COLOR = isDark ? '#9A9AA5' : '#404040';

  return (
    <motion.nav
      data-testid="guest-bottom-nav"
      className="fixed left-0 right-0 z-[9999]"
      animate={{ height: effectiveCompact ? 58 : 72 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32 }}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)', pointerEvents: 'none' }}
    >
      <div
        className="mx-auto w-[calc(100%-24px)] max-w-[380px] h-full px-3"
        style={{ background: 'var(--nav-surface)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderRadius: 999, boxShadow: 'var(--nav-shadow)', border: '1px solid var(--card-border)', pointerEvents: 'auto' }}
      >
        <motion.div
          className="flex items-center justify-around h-full"
          animate={{ scale: effectiveCompact ? 0.88 : 1 }}
          transition={{ type: 'spring', stiffness: 320, damping: 32 }}
          style={{ transformOrigin: 'center center' }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const color = isActive ? ACTIVE_COLOR : DEFAULT_COLOR;

            return (
              <button
                key={tab.id}
                onClick={() => {
                  tapVibrate();
                  if (tab.id === 'chat') {
                    acknowledgeMessageBadge(latestUnreadMarker);
                  }
                  // If this is the already-active tab and the user is on
                  // the same path, smooth-scroll to the top instead of
                  // re-navigating.
                  if (isActive && location.pathname === tab.path) {
                    if (jumpToTop()) return;
                  }
                  navigate(tab.path);
                }}
                className="flex items-center justify-center"
                style={{ width: 48, height: 48, background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'relative' }}
                data-testid={tab.testid}
                aria-label={tab.label}
              >
                <NavIconBump isActive={isActive}>
                  {tab.id === 'profile' ? (
                    <DefaultAvatar isActive={isActive} />
                  ) : tab.id === 'home' ? (
                    <LogoIcon isActive={isActive} />
                  ) : (
                    <tab.Icon color={color} />
                  )}
                </NavIconBump>
                {tab.id === 'chat' && hasUnread && !isChatTabActive ? <UnreadBadge count={unreadCount} dataTestId="guest-bottom-nav-chat-unread-dot" /> : null}
                {isActive && (
                  <NavActivePill
                    layoutId="guest-bottom-nav-indicator"
                    dataTestId="guest-bottom-nav-active-indicator"
                  />
                )}
              </button>
            );
          })}
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default GuestBottomNav;
